import { capitalize } from '../../util/formatters';

export type StateDictionary = { [id: number]: RequestState };
export type StringStateDictionary = { [key: string]: RequestState };

export type Action = {
  type: string;
  meta?: any;
  payload: any;
  error?: any;
};

export const StateStatus = {
  notAttempted: {
    success: false,
    loading: false,
    error: false,
    dirty: null,
  },
  loading: {
    success: false,
    loading: true,
    error: false,
    dirty: null,
  },
  success: {
    success: true,
    loading: false,
    error: false,
    dirty: null,
  },
  error: {
    success: false,
    loading: false,
    error: true,
    dirty: null,
  },
  dirty: {
    success: false,
    loading: false,
    error: false,
    dirty: true,
  },
};

export type RequestState = ValueOf<typeof StateStatus>;

export const shouldStatusExecute = (status: RequestState | null, resourceIsLoaded?: boolean) => {
  // IF (01/22/20): This first condition is specifically for the case when we're loading a single
  // resource. If it's in the store but there's no associated state object in resourcesStateById for
  // its id, it's possible it was loaded by some other parameter and we don't have to refetch it.
  if (resourceIsLoaded && !status) {
    return false;
  }
  if (!status || status.dirty) {
    return true;
  }
  if (status.success || status.loading || status.error) {
    return false;
  }
  return true;
};

/**
 * This function manages putting data into the state. The general flow is:
 *  1. If loading is true, add an entry in the state to say we're loading this data
 *  2. If there is data, then add it to the state under state.key = data. Also, set the statusObjectKey to success:true
 *  3. If we aren't loading, and there isn't data, then there must be an error, so set the status error = true.
 *
 * @param {object} state - the state
 * @param {string} key - the key under which the data should be stored. This is also used to create a status key, defined as `${key}Status`
 * @param {boolean} loading - a boolean of whether or not the status is being loaded
 * @param {any?} data - the data to store at @param key
 * @returns the new state
 */
export const updateStatus = (state: any, key: string, loading: boolean, data?: any) => {
  const statusObjectKey = `${key}Status`;
  if (loading) {
    return { ...state, [statusObjectKey]: StateStatus.loading };
  }
  if (data) {
    return { ...state, [key]: data, [statusObjectKey]: StateStatus.success };
  }
  return { ...state, [statusObjectKey]: StateStatus.error };
};

export const setStatusDirty = (state: any, key: string) => {
  const statusObjectKey = `${key}Status`;
  return { ...state, [statusObjectKey]: StateStatus.dirty };
};

/**
 * Handles putting data into the store when it's loaded by a certain param. eg employees loaded by orgId
 *  1. If loading is true, set the state to "loading"
 *  2. If there is data, merge it with the store, and set the state to "success"
 *  3. If we aren't loading, and there isn't data, then there must be an error, so set the state to "error"
 *
 * @param {object} state - the redux store
 * @param {string} resourceObject - the domain model being updated e.g. employeesById
 * @param {Object} resourceStateObject - the parameter the resource was loaded by e.g. employeesStateByOrgId
 * @param {Object} resourceStateKey - the id or value of the parameter e.g. 5
 * @param {boolean} loading - a boolean of whether or not the status is being loaded
 * @param {any?} data - the data to store at resource
 */
const updateState = (
  state: any,
  resourceObject: string,
  resourceStateObject: string,
  resourceStateKey: string,
  loading: boolean,
  data?: any,
) => {
  if (loading) {
    return {
      ...state,
      [resourceStateObject]: {
        ...state[resourceStateObject],
        [resourceStateKey]: StateStatus.loading,
      },
    };
  }

  if (data) {
    return {
      ...state,
      [resourceObject]: { ...state[resourceObject], ...data },
      [resourceStateObject]: {
        ...state[resourceStateObject],
        [resourceStateKey]: StateStatus.success,
      },
    };
  }

  return {
    ...state,
    [resourceStateObject]: { ...state[resourceStateObject], [resourceStateKey]: StateStatus.error },
  };
};

/**
 * @param {object} state - the redux store
 * @param {string} resource - the domain model being updated e.g. jobs, applicants, employees, etc
 * @param {Object} paramKey - the parameter the resource was loaded by e.g. orgId
 * @param {Object} paramValue - the id or value of the parameter
 * @param {string} deleteKey - the key (id) to delete
 */
export const deleteResourceByParam = (
  state: any,
  resource: string,
  paramKey: string,
  paramValue: string,
  deleteKey: string,
) => {
  const resourceObject = `${resource}ById`;
  const resourceStateByParamObject = `${resource}StateBy${capitalize(paramKey)}`;
  const resourceObjectWithDeletion = { ...state[resourceObject] };
  delete resourceObjectWithDeletion[deleteKey];

  return {
    ...state,
    [resourceObject]: { ...resourceObjectWithDeletion },
    [resourceStateByParamObject]: {
      ...state[resourceStateByParamObject],
      [paramValue]: StateStatus.success,
    },
  };
};

/**
 * Use when storing "ById", use updateResourceByObjectKey when storing by specific id (e.g. "...ByOrgId", etc.)
 * @param {object} state - the redux store
 * @param {string} resource - the domain model being updated e.g. jobs, applicants, employees, etc
 * @param {Object} paramKey - the parameter the resource was loaded by e.g. orgId
 * @param {Object} paramValue - the id or value of the parameter
 * @param {boolean} loading - a boolean of whether or not the status is being loaded
 * @param {any?} data - the data to store at resource
 */
export const updateResourceByParam = (
  state: any,
  resource: string,
  paramKey: string,
  paramValue: string,
  loading: boolean,
  data?: any,
) => {
  const resourceObject = `${resource}ById`;
  const resourceStateByParamObject = `${resource}StateBy${capitalize(paramKey)}`;
  return updateState(state, resourceObject, resourceStateByParamObject, paramValue, loading, data);
};

/**
 * Use when storing under specific ids rather than the more generic "ById" pattern
 * @param {object} state - the redux store
 * @param {string} stateObjectKey - the key where the data is stored in state in the format <resource>By<model id> e.g. applicantSummaryByJobId
 * @param {Object} paramValue - the id or value of the parameter
 * @param {boolean} loading - a boolean of whether or not the status is being loaded
 * @param {any?} data - the data to store at resource
 */
export const updateResourceByObjectKey = (
  state: any,
  stateObjectKey: string,
  paramValue: string,
  loading: boolean,
  data?: any,
) => {
  const [resource, paramKey] = stateObjectKey.split('By');
  const resourceStateByParamObject = `${resource}StateBy${paramKey}`;
  return updateState(state, stateObjectKey, resourceStateByParamObject, paramValue, loading, data);
};

/**
 * @param {object} state - the redux store
 * @param {string} resource - the domain model being updated e.g. jobs, applicants, employees, etc
 * @param {Object} paramKey - the parameter the resource was loaded by e.g. orgId
 * @param {Object} paramValue - the id or value of the parameter
 * @param {boolean} loading - a boolean of whether or not the status is being loaded
 * @param {any?} data - the data to store at resource
 */
export const replaceResourceByParam = (
  state: any,
  resource: string,
  paramKey: string,
  paramValue: string,
  data?: any,
) => {
  const resourceObject = `${resource}ById`;
  const newState = { ...state };
  const resourceIdsForParam = Object.keys(newState[resourceObject]).filter(
    (resourceId) => newState[resourceObject][resourceId][paramKey] === paramValue,
  );
  resourceIdsForParam.forEach((resourceId) => delete newState[resourceObject][resourceId]);
  const resourceStateByParamObject = `${resource}StateBy${capitalize(paramKey)}`;
  return updateState(newState, resourceObject, resourceStateByParamObject, paramValue, false, data);
};

/**
 * This function should only be used if updateResourceByParam isn't appropriate
 * @param {object} state - the state
 * @param {string} key - the key under which the data should be stored. This is also used to create a status key, defined as `${key}Status`
 * @param {boolean} loading - a boolean of whether or not the status is being loaded
 * @param {number} id - the id to store the status and data under. Used in state.key.id, and state.keyStatus.id
 * @param {any?} data - the data to store at @param key
 */
export const updateStatusById = (
  state: any,
  key: string,
  loading: boolean,
  id: string,
  data?: any,
) => updateState(state, key, `${key}Status`, id, loading, { [id]: data });

export const setStateDirtyByParam = (
  state: any,
  resource: string,
  paramKey: string,
  paramValue: string,
) => {
  const resourceStateObject = `${resource}StateBy${capitalize(paramKey)}`;

  return {
    ...state,
    [resourceStateObject]: { ...state[resourceStateObject], [paramValue]: StateStatus.dirty },
  };
};

export const setStatusDirtyById = (state: any, key: string, id: string) => {
  const statusObjectKey = `${key}Status`;

  return { ...state, [statusObjectKey]: { ...state[statusObjectKey], [id]: StateStatus.dirty } };
};

export const setStatusByParam = (
  state: any,
  resource: string,
  paramKey: string,
  paramValue: string,
  status: RequestState,
) => {
  const resourceStateObject = `${resource}StateBy${capitalize(paramKey)}`;

  return {
    ...state,
    [resourceStateObject]: { ...state[resourceStateObject], [paramValue]: status },
  };
};
